/**
 * @file The progress step of a multi-part Signify fetch: the fixed distributor, the derived header,
 * the live part table and the actions. Consumer: `SignifyFetchJobModalContent`.
 * It subscribes to two job leaves — the distributor, which never changes for a job, and `phase` —
 * and to nothing from the parts collection. That is deliberate: a shell that re-rendered on part
 * writes would hand the virtual table a new element up to a thousand times, so the header, the
 * footer and each cell subscribe for themselves instead.
 * The distributor is shown and disabled rather than hidden, because a fetch is bound to the
 * distributor it was started with and changing it is a new job.
 * A failed row's own Retry and the footer's press the same callable, so both take their disabled
 * reason from `signifyFetchJobRetryDisabledReason`. A row always has one thing to reset — itself —
 * so the rule's stall exception cannot bite here and the job's own `phase` stands in for the derived
 * status; `phase` is a leaf the dispatch touches two or three times a job, where the heartbeat that
 * derives `stalled` is written every few seconds and would repaint this shell with it.
 */

import { FC } from 'react';
import { Typography } from 'antd';
import { useSnapshot } from 'valtio';
import { DEFAULT_SIGNIFY_CUSTOMER_ID } from '#types';
import FlexCol from '@/components/atoms/FlexCol';
import { useSignifyDistributors } from '../../../api';
import SignifyDistributorSelector from '../../SignifyProductModal/components/SignifyDistributorSelector';
import SignifyFetchJobPartTable from './SignifyFetchJobPartTable';
import SignifyFetchJobProgressActions from './SignifyFetchJobProgressActions';
import SignifyFetchJobProgressHeader from './SignifyFetchJobProgressHeader';
import { createSignifyFetchJobStores, signifyFetchJobRetryDisabledReason } from '../use/services';
import { useSignifyFetchJobInsert, useSignifyFetchJobProgress } from '../use';
import type { SignifyFetchJobModalProps } from '../index.types';

const { Text } = Typography;

const FIXED_DISTRIBUTOR_REASON =
  'Distributor is fixed for a running fetch. Start a new list to change it.';

type SignifyFetchJobProgressProps = Pick<
  SignifyFetchJobModalProps,
  'targetKey' | 'disabled' | 'onSuccess' | 'onManualInsert' | 'onFindInsertedRows'
> & {
  jobId: string;
  userId: string;
};

const SignifyFetchJobProgress: FC<SignifyFetchJobProgressProps> = ({
  jobId,
  userId,
  targetKey,
  disabled,
  onSuccess,
  onManualInsert,
  onFindInsertedRows,
}) => {
  const { job: jobStore, parts: partsStore } = createSignifyFetchJobStores(jobId, userId);
  const {
    picks,
    saveRecord,
    canWriteRecords,
    onSaveRecordChange,
    retrying,
    cancelling,
    onRetry,
    onRetryPart,
    onCancel,
    onNewList,
  } = useSignifyFetchJobProgress({ jobId, targetKey });
  const { inserting, runInsert } = useSignifyFetchJobInsert({
    jobId,
    partsStore,
    saveRecord,
    disabled,
    onSuccess,
    onManualInsert,
    onFindInsertedRows,
  });

  const jobSnap = useSnapshot(jobStore.state);
  const distributorId = jobSnap.data?.distributorId ?? '';
  const {
    allDistributors,
    distributors: filteredDistributors,
    loading: loadingDistributors,
    searchTerm: distributorSearchTerm,
    setSearchTerm: setDistributorSearchTerm,
    refreshDistributors,
    totalCount: totalDistributors,
    filteredCount: filteredDistributorsCount,
  } = useSignifyDistributors(DEFAULT_SIGNIFY_CUSTOMER_ID, true);

  const distributorName =
    allDistributors.find((distributor) => distributor.uid === distributorId)?.name ?? distributorId;
  const retryDisabledReason = signifyFetchJobRetryDisabledReason({
    retrying,
    retryable: 1,
    status: jobSnap.data?.phase ?? 'queued',
  });

  return (
    <FlexCol gap={10}>
      <FlexCol gap={0}>
        <SignifyDistributorSelector
          selectedDistributor={distributorId}
          distributorSearchValue={distributorName}
          loading
          loadingDistributors={loadingDistributors}
          filteredDistributors={filteredDistributors}
          filteredDistributorsCount={filteredDistributorsCount}
          totalDistributors={totalDistributors}
          distributorSearchTerm={distributorSearchTerm}
          onDistributorSearch={setDistributorSearchTerm}
          onDistributorSelect={() => {}}
          onDistributorClear={() => {}}
          onRefreshDistributors={refreshDistributors}
        />
        <Text type="secondary">{FIXED_DISTRIBUTOR_REASON}</Text>
      </FlexCol>

      <SignifyFetchJobProgressHeader jobStore={jobStore} partsStore={partsStore} />

      <SignifyFetchJobPartTable
        partsStore={partsStore}
        picks={picks}
        retryDisabledReason={retryDisabledReason}
        onRetryPart={onRetryPart}
      />

      <SignifyFetchJobProgressActions
        jobStore={jobStore}
        partsStore={partsStore}
        picks={picks}
        inserting={inserting}
        saveRecord={saveRecord}
        canWriteRecords={canWriteRecords}
        disabled={disabled}
        retrying={retrying}
        cancelling={cancelling}
        onSaveRecordChange={onSaveRecordChange}
        onInsert={runInsert}
        onRetry={onRetry}
        onCancel={onCancel}
        onNewList={onNewList}
      />
    </FlexCol>
  );
};

export default SignifyFetchJobProgress;
